import React, { useState } from 'react';
import axios from 'axios';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useNavigate } from 'react-router-dom';
import { LiaEyeSlashSolid, LiaEyeSolid } from 'react-icons/lia';
import { FaUser, FaLock, FaSignInAlt, FaSpinner, FaShieldAlt } from 'react-icons/fa';
import logo from '../Header/Images/RMB-Strip-Jaipur.png';
import mainLogo from '../Header/Images/logo.png';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [adminRoleModal, setAdminRoleModal] = useState<{ name: string; role: string } | null>(null);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        'https://rmbbackend.rmbjaipur.com/auth/login',
        { name: username, password },
        { withCredentials: true }
      );

      const token = response.data.token;
      const user = response.data.user || {};
      const uRole = (user.role || response.data.role || '').toString().toLowerCase();

      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      if (uRole === 'admin' || uRole === 'super-admin' || uRole === 'superadmin') {
        setAdminRoleModal({ name: user.name || username, role: user.role || uRole });
        setLoading(false);
        return;
      }

      const targetDashboard = response.data.dashboard || '/memberportal';
      window.location.href = targetDashboard;
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Invalid username or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#0A111E] via-[#0A2451] to-[#0F172A] text-slate-100 font-sans">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8 relative">
        {/* Subtle Background Glows */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="w-full max-w-md bg-[#131E32]/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-[#2A3B5C] relative z-10">
          
          {/* Header Banner with RMB Emblem */}
          <div className="bg-gradient-to-r from-[#0A2451] via-[#1E3A8A] to-[#0A2451] p-6 sm:p-8 text-center text-white border-b border-[#2A3B5C] relative">
            <div className="bg-white p-2.5 sm:p-3 rounded-2xl shadow-xl flex items-center justify-center border border-slate-200 mb-4 max-w-full overflow-hidden mx-auto">
              <img
                src={logo}
                alt="Rotary RMB Jaipur Logo"
                className="h-9 sm:h-11 w-auto max-w-full object-contain"
              />
            </div>
            <h1 className="text-xl sm:text-2xl font-black tracking-wide text-white">Member Portal Login</h1>
            <p className="text-xs text-slate-300 mt-1 font-medium">Sign in to access your RMB Chapter Dashboard</p>
          </div>

          {/* Form */}
          <div className="p-6 sm:p-8 space-y-6">
            {error && (
              <div className="bg-red-950/80 border-l-4 border-red-500 p-4 rounded-xl text-red-200 text-xs font-semibold animate-fade-in flex items-center gap-2 border border-red-500/30">
                <FaShieldAlt className="text-red-400 flex-shrink-0" size={16} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username Input */}
              <div>
                <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-300 mb-1.5">
                  Username / Email / Mobile
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FaUser size={14} />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3.5 bg-[#0B1220] border border-[#2A3B5C] rounded-2xl text-sm font-semibold text-white placeholder-slate-500 outline-none focus:border-[#FDBC12] focus:ring-2 focus:ring-[#FDBC12]/20 transition"
                    placeholder="Enter Username or Email"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FaLock size={14} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-11 py-3.5 bg-[#0B1220] border border-[#2A3B5C] rounded-2xl text-sm font-semibold text-white placeholder-slate-500 outline-none focus:border-[#FDBC12] focus:ring-2 focus:ring-[#FDBC12]/20 transition"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-amber-400 transition"
                  >
                    {showPassword ? <LiaEyeSolid size={20} /> : <LiaEyeSlashSolid size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#FDBC12] via-amber-400 to-amber-500 hover:from-amber-400 hover:to-amber-300 text-[#0A2451] font-black rounded-2xl text-sm shadow-xl hover:shadow-2xl transition duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" size={16} /> Authenticating...
                  </>
                ) : (
                  <>
                    <FaSignInAlt size={16} /> Sign In to Member Portal
                  </>
                )}
              </button>
            </form>

            <div className="text-center pt-4 border-t border-[#2A3B5C]">
              <p className="text-xs text-slate-400 font-medium">
                Not registered yet?{" "}
                <a href="/register" className="font-extrabold text-[#FDBC12] hover:underline transition">
                  Apply for Membership →
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* ADMIN / SUPER ADMIN ROLE SELECTION MODAL */}
      {adminRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200 text-center space-y-6">
            <div className="w-16 h-16 bg-purple-100 text-purple-700 rounded-full mx-auto flex items-center justify-center text-3xl shadow-inner">
              👑
            </div>
            <div>
              <span className="px-3 py-1 bg-amber-100 text-amber-900 border border-amber-300 font-extrabold text-[11px] rounded-full uppercase tracking-wider">
                {adminRoleModal.role} Privileges Detected
              </span>
              <h2 className="text-xl font-black text-slate-900 mt-2">Welcome, {adminRoleModal.name}!</h2>
              <p className="text-xs text-slate-600 mt-1 font-medium">
                You have dual access to both the Admin Panel and the Member Portal. Select how you want to act for this session:
              </p>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => window.location.href = '/dashboard'}
                className="w-full py-3.5 px-4 bg-[#0A2451] hover:bg-blue-900 text-white font-extrabold rounded-xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                🛡️ Act as Admin (Open Admin Panel)
              </button>

              <button
                type="button"
                onClick={() => window.location.href = '/user-dashboard'}
                className="w-full py-3.5 px-4 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                👤 Act as Member (Open Member Portal)
              </button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold">
              💡 Tip: You can switch between Admin & Member views anytime using the top header bar button!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
